/**
 * Firebase Cloud Messaging (FCM) Service
 * 
 * This service handles FCM configuration, token management, and device registration
 * for push notifications in the Aris application.
 */

import { axiosProfileInstance } from '../AxiosConfiguration';

class FCMService {
  constructor() {
    this.messaging = null;
    this.fcmConfig = null;
    this.currentToken = null;
    this.isInitialized = false;
    this.isInitializing = false; // Prevent concurrent initialization
    this.tokenRefreshInterval = null;
    this.deviceId = null;
    this.lastRegisteredToken = null;
    this.swRegistration = null;
  }

  /**
   * Build options for messaging.getToken(), omitting VAPID on Safari
   */
  getTokenOptions() {
    const options = {};
    try {
      // Only pass VAPID for non-Safari browsers
      if (!this.isSafari() && this.fcmConfig?.vapidKey) {
        options.vapidKey = this.fcmConfig.vapidKey;
      }
      // Pass SW registration when available and capable
      const sw = (this.swRegistration && this.swRegistration.pushManager) ? this.swRegistration : undefined;
      if (sw) {
        options.serviceWorkerRegistration = sw;
      }
    } catch (_) { }
    return options;
  }

  /**
   * Load FCM configuration from environment variables
   */
  loadFCMConfig() {
    try {
      this.fcmConfig = {
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: process.env.REACT_APP_FIREBASE_APP_ID,
        measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY
      };

      // Validate required fields
      const requiredFields = ['apiKey', 'authDomain', 'projectId', 'messagingSenderId', 'appId'];
      const missingFields = requiredFields.filter(field => !this.fcmConfig[field]);

      if (missingFields.length > 0) {
        throw new Error(`Missing required Firebase config fields: ${missingFields.join(', ')}`);
      }

      return this.fcmConfig;
    } catch (error) {
      console.error('Error loading FCM config from environment:', error);
      throw error;
    }
  }

  /**
   * Initialize Firebase and FCM
   */
  async initialize() {
    try {
      // Prevent multiple initializations
      if (this.isInitialized) {
        return true;
      }

      // Prevent concurrent initialization attempts
      if (this.isInitializing) {
        while (this.isInitializing) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        return this.isInitialized;
      }

      this.isInitializing = true;

      // Check if Firebase is available
      if (typeof window === 'undefined' || !window.firebase) {
        console.warn('Firebase SDK not loaded. FCM initialization skipped.');
        this.isInitializing = false;
        return false;
      }

      // Check if messaging is supported (Safari compatibility)
      if (!window.firebase.messaging || !window.firebase.messaging.isSupported()) {
        console.warn('Firebase messaging not supported in this browser. FCM initialization skipped.');
        this.isInitializing = false;
        return false;
      }

      // Load FCM configuration from environment variables
      this.loadFCMConfig();

      // Initialize Firebase if not already initialized
      if (!window.firebase.apps.length) {
        window.firebase.initializeApp(this.fcmConfig);
      }

      // Initialize messaging with error handling
      try {
        this.messaging = window.firebase.messaging();
      } catch (messagingError) {
        console.error('Error initializing Firebase messaging:', messagingError);
        return false;
      }

      // Set up service worker
      await this.setupServiceWorker();

      // Check current permission status but don't request automatically
      await this.checkPermissionAndGetToken();

      // Set up message handlers
      this.setupMessageHandlers();

      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('Error initializing FCM service:', error);
      return false;
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Set up service worker for background message handling
   */
  async setupServiceWorker() {
    if ('serviceWorker' in navigator) {
      try {
        // Register service worker
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        this.swRegistration = registration;

        // Wait for service worker to be ready
        await navigator.serviceWorker.ready;

        // Send Firebase config to service worker
        this.sendConfigToServiceWorker();

        // Use the service worker with messaging
        if (this.messaging && this.messaging.useServiceWorker) {
          this.messaging.useServiceWorker(registration);
        }
      } catch (error) {
        console.error('FCM Service Worker registration failed:', error);
      }
    }
  }

  /**
   * Send Firebase config to service worker
   */
  async sendConfigToServiceWorker() {
    try {
      // Wait for service worker to be ready and controlling
      const registration = await navigator.serviceWorker.ready;

      // Get the active service worker
      const sw = registration.active || registration.installing || registration.waiting;

      if (!sw) {
        console.error('No service worker available');
        return;
      }

      const config = {
        type: 'FIREBASE_CONFIG',
        config: {
          apiKey: this.fcmConfig.apiKey,
          authDomain: this.fcmConfig.authDomain,
          projectId: this.fcmConfig.projectId,
          storageBucket: this.fcmConfig.storageBucket,
          messagingSenderId: this.fcmConfig.messagingSenderId,
          appId: this.fcmConfig.appId,
          measurementId: this.fcmConfig.measurementId
        }
      };

      // Send to active controller if available
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage(config);
      } else {
        // If no controller yet, send directly to the service worker
        sw.postMessage(config);

        // Also listen for when SW becomes controller
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage(config);
          }
        }, { once: true });
      }
    } catch (error) {
      console.error('Error sending config to service worker:', error);
    }
  }

  /**
   * Ensure we have an active ServiceWorkerRegistration with pushManager
   */
  async ensureSWReady() {
    try {
      if (this.swRegistration && this.swRegistration.active && this.swRegistration.pushManager) {
        return this.swRegistration;
      }

      // Prefer existing registration for our file
      const reg = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
      if (reg && reg.active) {
        this.swRegistration = reg;
      } else {
        // Wait for ready which yields the active controlling SW
        const ready = await navigator.serviceWorker.ready;
        this.swRegistration = ready || this.swRegistration;
      }

      return this.swRegistration;
    } catch (e) {
      console.warn('ensureSWReady failed; continuing without explicit SW registration', e);
      return this.swRegistration;
    }
  }


  /**
   * Check current permission status and get token if already granted
   * This method doesn't request permission - just checks existing status
   */
  async checkPermissionAndGetToken() {
    try {
      const permission = Notification.permission;

      if (permission === 'granted') {

        // Get FCM token
        await this.ensureSWReady();
        let token;
        try {
          token = await this.messaging.getToken(this.getTokenOptions());
        } catch (e) {
          // Retry once after ensuring SW ready to avoid pushManager undefined
          await this.ensureSWReady();
          token = await this.messaging.getToken(this.getTokenOptions());
        }

        if (token) {
          this.currentToken = token;

          // Only register if needed (new device or token changed)
          if (this.needsDeviceRegistration(token)) {
            await this.registerTokenWithServer(token);
          }
          return token;
        } else {
          return null;
        }
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error checking FCM permission/token:', error);
      throw error;
    }
  }

  /**
   * Request notification permission and get FCM token
   * This method MUST be called from a user gesture (click, touch, etc.)
   */
  async requestPermissionAndGetToken() {
    try {
      // Request permission - this must be called from user gesture
      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        // Get FCM token
        await this.ensureSWReady();
        let token;
        try {
          token = await this.messaging.getToken(this.getTokenOptions());
        } catch (e) {
          await this.ensureSWReady();
          token = await this.messaging.getToken(this.getTokenOptions());
        }

        if (token) {
          this.currentToken = token;

          // Only register if needed (new device or token changed)
          if (this.needsDeviceRegistration(token)) {
            await this.registerTokenWithServer(token);
          } else {
          }
          return token;
        } else {
          return null;
        }
      } else {
        return null;
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Register FCM token with server
   */
  async registerTokenWithServer(token) {
    try {
      const deviceInfo = this.getDeviceInfo();

      const response = await axiosProfileInstance.post('/api/user/fcm/register-device', {
        token: token,
        platform: 'web',
        device_id: deviceInfo.deviceId,
        user_agent: navigator.userAgent,
        app_version: deviceInfo.appVersion
      }, {
        headers: {
          'User-Agent': navigator.userAgent
        }
      });


      // Save the registered token to avoid duplicate registrations
      this.setLastRegisteredToken(token);

      return response.data;
    } catch (error) {
      console.error('Error registering FCM token:', error);
      throw error;
    }
  }

  /**
   * Set up message handlers for foreground and background messages
   */
  setupMessageHandlers() {
    if (!this.messaging) return;

    // Handle foreground messages - let SW show native notification; we only emit app event
    if (typeof this.messaging.onMessage === 'function') {
      this.messaging.onMessage((payload) => {
        // Emit custom event for app to handle
        this.emitNotificationEvent('message-received', payload);
      });
    } else {
      console.warn('onMessage not available - this may be due to browser compatibility');
    }

    // Handle token refresh - using modern approach
    // Note: onTokenRefresh is deprecated in newer Firebase versions
    // Token refresh is now handled automatically by Firebase
    // We can periodically check for token changes if needed
    this.setupTokenRefreshMonitoring();
  }

  /**
   * Display browser notification in foreground
   */
  displayNotification(payload) {
    const { notification, data } = payload;

    // Only show browser notification if permission is granted
    if (Notification.permission !== 'granted') {
      return;
    }

    // Extract click action from various possible sources
    const clickAction = notification?.click_action ||
      data?.redirect_link ||
      data?.click_action ||
      this.getDefaultClickAction(data);

    // Base options used for both SW and page notifications
    const baseOptions = {
      body: notification?.body || data?.content || 'You have a new notification',
      icon: notification?.icon || '/arisLogo.png',
      image: notification?.image,
      badge: '/arisLogo.png',
      tag: `aris-${data?.notification_type || 'general'}-${data?.notification_subtype || 'default'}`,
      requireInteraction: true,
      silent: false,
      data: {
        click_action: clickAction,
        notification_type: data?.notification_type,
        notification_subtype: data?.notification_subtype,
        request_id: data?.request_id,
        timestamp: data?.timestamp || Date.now(),
        original_payload: JSON.stringify(payload)
      }
    };

    const title = notification?.title || data?.subject || 'New Notification';

    // Prefer Service Worker notifications (support actions), fallback to page Notification without actions
    try {
      if (this.swRegistration && this.swRegistration.showNotification) {
        const swOptions = {
          ...baseOptions,
          actions: [
            { action: 'view', title: 'View', icon: '/arisLogo.png' },
            { action: 'dismiss', title: 'Dismiss', icon: '/arisLogo.png' },
          ],
        };
        this.swRegistration.showNotification(title, swOptions);
      } else {
        // Actions are not supported with window.Notification; omit them
        const pageOptions = { ...baseOptions };
        const notificationInstance = new Notification(title, pageOptions);

        notificationInstance.onclick = (event) => {
          event.preventDefault();
          notificationInstance.close();
          window.focus();
          this.handleNotificationClick(data, clickAction);
        };

        setTimeout(() => {
          try { notificationInstance.close(); } catch (_) { }
        }, 10000);
      }
    } catch (error) {
      console.error('Error displaying browser notification:', error);
    }
  }

  /**
   * Set up token refresh monitoring (modern approach)
   */
  setupTokenRefreshMonitoring() {
    // Check for token changes periodically
    // This is a fallback since onTokenRefresh is deprecated
    this.tokenRefreshInterval = setInterval(async () => {
      try {
        if (this.messaging && this.fcmConfig) {
          await this.ensureSWReady();
          let currentToken;
          try {
            currentToken = await this.messaging.getToken(this.getTokenOptions());
          } catch (_) {
            await this.ensureSWReady();
            currentToken = await this.messaging.getToken(this.getTokenOptions());
          }

          if (currentToken && currentToken !== this.currentToken) {
            this.currentToken = currentToken;

            // Only register if needed (token changed)
            if (this.needsDeviceRegistration(currentToken)) {
              await this.registerTokenWithServer(currentToken);
            }
          }
        }
      } catch (error) {
        console.error('Error checking token refresh:', error);
      }
    }, 60000); // Check every minute
  }

  /**
   * Detect Safari browser (exclude Chrome/Chromium on iOS/macOS)
   */
  isSafari() {
    try {
      const ua = navigator.userAgent || '';
      const vendor = navigator.vendor || '';
      const isSafariEngine = /Safari/.test(ua) && /Apple Computer/.test(vendor);
      const isChromeLike = /Chrome|Chromium|CriOS|Edg|OPR/.test(ua);
      return isSafariEngine && !isChromeLike;
    } catch (_) {
      return false;
    }
  }

  /**
   * Get default click action based on notification type
   */
  getDefaultClickAction(data) {
    const subtype = data?.notification_subtype || data?.subtype;

    switch (subtype) {
      case 'relationship_request':
        return data?.request_id ? `/dashboard?request=${data.request_id}` : '/chat?tab=requests';
      case 'coaching_session':
        return '/coach/individual';
      case 'assessment_reminder':
        return '/onboarding';
      case 'subscription_reminder':
        return '/manageMembership';
      default:
        return '/dashboard';
    }
  }

  /**
   * Handle notification click
   */
  handleNotificationClick(data, clickAction) {
    // Focus window
    window.focus();

    // Navigate to appropriate page based on click_action
    if (clickAction) {
      if (clickAction.startsWith('http')) {
        // External URL - navigate to it
        window.location.href = clickAction;
      } else {
        // Internal route - check if we need to navigate
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath !== clickAction) {
          window.location.href = clickAction;
        }
      }
    }

    // Emit custom event for app to handle
    this.emitNotificationEvent('notification-clicked', {
      ...data,
      click_action: clickAction
    });
  }

  /**
   * Emit custom events for the application to handle
   */
  emitNotificationEvent(type, data) {
    const customEvent = new CustomEvent(`aris-fcm-${type}`, {
      detail: {
        type,
        data,
        timestamp: Date.now()
      }
    });

    window.dispatchEvent(customEvent);
  }

  /**
   * Get device information
   */
  getDeviceInfo() {
    return {
      deviceId: this.generateDeviceId(),
      appVersion: this.getAppVersion(),
      userAgent: navigator.userAgent,
      platform: 'web'
    };
  }

  /**
   * Generate unique device ID (persistent across browser sessions)
   */
  generateDeviceId() {
    if (this.deviceId) {
      return this.deviceId;
    }

    let deviceId = localStorage.getItem('aris-device-id');

    if (!deviceId) {
      // Create a more robust device ID using browser fingerprinting
      const browserInfo = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timestamp: Date.now()
      };

      // Create a hash-like string from browser info
      const browserFingerprint = btoa(JSON.stringify(browserInfo))
        .replace(/[^a-zA-Z0-9]/g, '')
        .substring(0, 12);

      deviceId = `web-${browserFingerprint}-${Math.random().toString(36).substr(2, 6)}`;
      localStorage.setItem('aris-device-id', deviceId);
    }

    this.deviceId = deviceId;
    return deviceId;
  }

  /**
   * Get last registered token for this device
   */
  getLastRegisteredToken() {
    return localStorage.getItem(`aris-registered-token-${this.generateDeviceId()}`);
  }

  /**
   * Set last registered token for this device
   */
  setLastRegisteredToken(token) {
    localStorage.setItem(`aris-registered-token-${this.generateDeviceId()}`, token);
    this.lastRegisteredToken = token;
  }

  /**
   * Check if device registration is needed
   */
  needsDeviceRegistration(currentToken) {
    if (!currentToken) return false;

    const lastRegisteredToken = this.getLastRegisteredToken();

    // Need to register if:
    // 1. Never registered before
    // 2. Token has changed
    return !lastRegisteredToken || lastRegisteredToken !== currentToken;
  }

  /**
   * Get app version
   */
  getAppVersion() {
    return document.querySelector('meta[name="app-version"]')?.content || '1.0.0';
  }

  /**
   * Unregister FCM token
   */
  async unregisterToken() {
    try {
      if (this.currentToken) {
        await axiosProfileInstance.delete('/api/user/fcm/remove-device', {
          data: {
            token: this.currentToken
          }
        });

        this.currentToken = null;
      }
    } catch (error) {
      console.error('Error unregistering FCM token:', error);
      throw error;
    }
  }

  /**
   * Update push notification settings
   */
  async updatePushNotificationSettings(enabled) {
    try {
      const response = await axiosProfileInstance.put('/api/user/fcm/push-notifications', {
        push_notifications: enabled
      });

      return response.data;
    } catch (error) {
      console.error('Error updating push notification settings:', error);
      throw error;
    }
  }

  /**
   * Get current FCM token
   */
  getCurrentToken() {
    return this.currentToken;
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus() {
    return Notification.permission;
  }

  /**
   * Check if FCM is initialized
   */
  isReady() {
    return this.isInitialized;
  }

  /**
   * Cleanup FCM service
   */
  cleanup() {
    // Clear token refresh interval
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }

    this.messaging = null;
    this.fcmConfig = null;
    this.currentToken = null;
    this.isInitialized = false;
    this.deviceId = null;
    this.lastRegisteredToken = null;
  }
}

// Create singleton instance
const fcmService = new FCMService();

export default fcmService;
